import { SpeechError, SpeechErrorCode, toSpeechError } from './errors'

/** RFC4122 v4，优先用平台实现 */
export function uuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  const rnd =
    typeof crypto !== 'undefined' && crypto.getRandomValues
      ? (n) => crypto.getRandomValues(new Uint8Array(n))
      : (n) => Array.from({ length: n }, () => Math.floor(Math.random() * 256))
  const b = rnd(16)
  b[6] = (b[6] & 0x0f) | 0x40
  b[8] = (b[8] & 0x3f) | 0x80
  const hex = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

/** 读响应体：优先 JSON，失败退回文本 */
export async function readBody(res) {
  const text = await res.text()
  try {
    return { json: JSON.parse(text), text }
  } catch (e) {
    return { json: null, text }
  }
}

/**
 * 带超时的 fetch，**响应体读取也在同一把超时保护内**。
 *
 * 只给 fetch() 加超时是不够的：响应头回来后 body 传输中途挂起（弱网、代理半关连接）时
 * fetch 的 promise 已经 resolve，res.text() 会无限期挂着，UI 永远退不出 loading。
 *
 * 超时抛 SpeechError(TIMEOUT)，网络层失败抛 SpeechError(NETWORK)。
 * 跨域被 CORS 拦下时浏览器只给 TypeError，同样落到 NETWORK。
 *
 * @returns {Promise<{res: Response, parsed: {json: object|null, text: string}}>}
 */
export async function fetchWithTimeout(url, init = {}, timeoutMs = 20000, extra) {
  if (typeof fetch !== 'function') {
    throw new SpeechError(SpeechErrorCode.UNSUPPORTED, 'fetch is not available', extra)
  }
  const controller = typeof AbortController === 'function' ? new AbortController() : null
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null
  try {
    const res = await fetch(url, controller ? { ...init, signal: controller.signal } : init)
    const parsed = await readBody(res)
    return { res, parsed }
  } catch (e) {
    throw toSpeechError(e, SpeechErrorCode.NETWORK, extra)
  } finally {
    if (timer) clearTimeout(timer)
  }
}

/** 把厂商 HTTP 状态映射为 SpeechError */
export function vendorHttpError(res, body, vendor) {
  const extra = { vendor, status: res.status, body: (body && body.text) || '' }
  if (res.status === 401 || res.status === 403) {
    return new SpeechError(
      SpeechErrorCode.VENDOR_REJECTED,
      `${vendor} rejected credential (HTTP ${res.status})`,
      extra
    )
  }
  return new SpeechError(
    SpeechErrorCode.VENDOR_FAILED,
    `${vendor} request failed (HTTP ${res.status})`,
    extra
  )
}

/** 转成 0-100 的数字；无法解析返回 undefined（上报时整个字段不传，勿传 0 顶替） */
export function toScore(v) {
  if (v === null || v === undefined || v === '') return undefined
  const n = Number(v)
  if (!Number.isFinite(n)) return undefined
  return Math.max(0, Math.min(100, Math.round(n * 100) / 100))
}

/** 只保留 JSON 可序列化且不超过 maxBytes 的内容，超了返回裁剪标记 */
export function limitJsonBytes(obj, maxBytes) {
  try {
    const s = JSON.stringify(obj)
    if (s.length <= maxBytes) return obj
    return { _truncated: true, _originalBytes: s.length }
  } catch (e) {
    return { _unserializable: true }
  }
}

/**
 * 清洗评测参考文本。
 *
 * 题目的 evaluate_text 里会夹带 `[[段落正文]]` 这类编辑标记，开头还可能有个全角冒号；
 * 这些字符原样送给厂商会被当成要朗读的内容，直接把准确度/完整度打歪。
 */
export function cleanRefText(text) {
  if (!text) return ''
  return String(text)
    .replace(/\[\[.*?\]\]/g, '')
    .replace(/^：/, '')
    .trim()
}
